import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { ToastTypes } from '../../../shared/services/abstract/toast.service.abstract';
import { BottomNavButton } from '../../../shared/bottom-nav/bottom-nav.component';
import {
  BottomNavService,
  BottomNavUpdateButtonsList,
  BottomNavUpdateButtonState,
  BottomNavUpdateState,
} from '../../../shared/bottom-nav/bottom-nav.service';
import { ToastService } from '../../../shared/services/toast.service';
import { ZiroIcons } from '../../../shared/models/ziro-icons.enum';
import { OAuth2ResourceServerSettings } from '../../../shared/models/generated/ziroModels';
import { ZiroFormConfig, ZiroFormsValidationState } from '../../../forms/ziro-forms-models';
import { ZiroFormStateService } from '../../../forms/ziro-form-state.service';
import { ButtonStyles, ButtonTypes } from '../../../button/button.component';
import { BreadcrumbsService } from '../../../shared/breadcrumbs/breadcrumbs.service';
import { HtmlInputType, ZiroTextConfig } from '../../../forms/fields/text/ziro-text.component';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { ZiroFormAbstractDirective } from '../../../forms/ziro-form-abstract.directive';
import { Oauth2ResourceServerSettingsResource } from '../../resources/oauth2-resource-server-settings.resource';
import { finalize, map, switchMap } from 'rxjs/operators';
import { ZiroModalService } from '../../../shared/services/ziro-modal.service';
import { isEqual } from 'lodash';

@Component({
  selector: 'ziro-oauth2-resource-server-management',
  templateUrl: './oauth2-resource-server-management.component.html',
  styleUrls: ['../../admin-page.scss'],
  standalone: false,
})
export class Oauth2ResourceServerManagementComponent
  extends ZiroFormAbstractDirective<OAuth2ResourceServerSettings>
  implements OnInit, OnDestroy
{
  private toastService = inject(ToastService);
  private ziroModalService = inject(ZiroModalService);
  private bottomNavService = inject(BottomNavService);
  protected _ziroFormStateService: ZiroFormStateService;
  private breadcrumbsService = inject(BreadcrumbsService);
  private oauth2ResourceServerSettingsResource = inject(Oauth2ResourceServerSettingsResource);

  isLoading = true;
  showDefaultsAlert = false;
  oauth2ResourceServerSettingsDefaults: OAuth2ResourceServerSettings;

  formConfig: ZiroFormConfig = {
    fields: {
      jwtIssuerUrl: {
        dataAutomation: 'oauth2-jwt-issuer-url',
        label: 'tkey;admin.system.oauth2_resource_server_management.jwt_issuer_url.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: () => !this.showDefaultsAlert,
        options: {
          content: 'tkey;admin.system.oauth2_resource_server_management.jwt_issuer_url.content',
        },
      },
      audience: {
        dataAutomation: 'oauth2-audience',
        label: 'tkey;admin.system.oauth2_resource_server_management.audience.label',
        componentConfig: new ZiroTextConfig({ htmlInputType: HtmlInputType.TEXT }),
        required: () => !this.showDefaultsAlert,
        options: {
          content: 'tkey;admin.system.oauth2_resource_server_management.audience.content',
        },
      },
    },
  };
  fieldIds: string[] = ['jwtIssuerUrl', 'audience'];

  private _subscriptions = new Subscription();

  ngOnInit(): void {
    this.breadcrumbsService.updateBreadcrumbs([{ label: 'tkey;admin.system.oauth2_resource_server_management.title' }]);

    forkJoin([
      this.oauth2ResourceServerSettingsResource.get(),
      this.oauth2ResourceServerSettingsResource.getDefaults(),
    ]).subscribe(([oauth2ResourceServerSettings, oauth2ResourceServerSettingsDefaults]) => {
      if (oauth2ResourceServerSettingsDefaults.jwtIssuerUrl && oauth2ResourceServerSettingsDefaults.audience) {
        this.showDefaultsAlert = true;
        this.oauth2ResourceServerSettingsDefaults = oauth2ResourceServerSettingsDefaults;
      }
      this.entitySource.next(oauth2ResourceServerSettings);
      this.setIsExisting(!!oauth2ResourceServerSettings.jwtIssuerUrl);
      this._initBottomNav();
      this.isLoading = false;
    });

    const formUpdateSub = this.ziroFormsUpdate$.subscribe((update) => {
      if (this.isFormSubmitted) {
        this._setBottomNavValidationError(!this.isFormValid());
      }
      if (!isEqual(update.new, update.old) && this.showDefaultsAlert) {
        // necessary in order to clear a validation error that occurs in both fields
        this.fieldChannels['jwtIssuerUrl'].validateSource.next();
        this.fieldChannels['audience'].validateSource.next();
      }
    });
    this._subscriptions.add(formUpdateSub);

    const formSubmitSub = this._validateAndSubmitSource.subscribe(() => {
      this._setBottomNavValidationError(!this.isFormValid());
    });
    this._subscriptions.add(formSubmitSub);
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    this._subscriptions.unsubscribe();
    this.breadcrumbsService.clearBreadcrumbs();
  }

  protected submit(): Observable<void> {
    return this._saveSettings();
  }

  private _saveSettings(): Observable<void> {
    this._setBottomNavValidationError(false);
    this._setBottomNavPending('save');

    if (
      (this.showDefaultsAlert && this.formData.audience && !this.formData.jwtIssuerUrl) ||
      (this.showDefaultsAlert && !this.formData.audience && this.formData.jwtIssuerUrl)
    ) {
      this.fieldComponents.forEach((fc) => {
        fc.validationState = ZiroFormsValidationState.INVALID;
        fc.validationMessage = {
          content: 'tkey;admin.system.oauth2_resource_server_management.default_values.validation',
          params: {},
        };
      });
      this._setBottomNavValidationError(true);
      this._setBottomNavPending('none');
      return of(null);
    }
    let areBothFieldsEmpty = false;
    if (this.showDefaultsAlert) {
      areBothFieldsEmpty = !this.formData.audience && !this.formData.jwtIssuerUrl;
    }

    return this.oauth2ResourceServerSettingsResource
      .validate(areBothFieldsEmpty ? this.oauth2ResourceServerSettingsDefaults : this.entity)
      .pipe(
        switchMap(() =>
          this.oauth2ResourceServerSettingsResource.put(
            areBothFieldsEmpty ? this.oauth2ResourceServerSettingsDefaults : this.entity,
          ),
        ),
        map(() => {
          this.setIsExisting(true);
          this.toastService.push(
            ToastTypes.SUCCESS,
            ZiroIcons.CONFIG,
            'tkey;shared.toast.save.success.title',
            'tkey;admin.system.oauth2_resource_server_management.toast.save.message',
          );
        }),
        finalize(() => {
          this._setBottomNavPending('none');
        }),
      );
  }

  private _openDeleteModal(): void {
    const options = {
      animation: false,
      modalViewProperties: {
        promptBody: 'tkey;admin.system.oauth2_resource_server_management.modal.delete.message',
        icon: ZiroIcons.DELETE_SMALL,
        iconClass: 'text-danger',
        displayCloseButton: true,
        buttons: [
          {
            label: 'tkey;dialogs.button.cancel',
            buttonClass: ButtonStyles.OUTLINE_DEFAULT,
            dataAutomation: 'confirmation-modal-cancel-button',
          },
          {
            label: 'tkey;dialogs.button.delete',
            buttonClass: ButtonStyles.OUTLINE_DANGER,
            dataAutomation: 'confirmation-modal-confirm-button',
            cb: () => {
              return this._deleteSettings();
            },
          },
        ],
      },
    };
    this.ziroModalService.openPromptModal(() => options.modalViewProperties, options);
  }

  private _deleteSettings(): Observable<void> {
    this._setBottomNavPending('delete');
    return this.oauth2ResourceServerSettingsResource.delete().pipe(
      map(() => {
        this.setIsExisting(false);
        this._setBottomNavPending('none');
        this.entitySource.next({
          jwtIssuerUrl: '',
          audience: '',
        });
        this._setIsFormSubmitted(false);
        this._ziroFormStateService.setIsFormDirty(false);
        this.toastService.push(
          ToastTypes.INFO,
          `${ZiroIcons.DELETE_SMALL} text-danger`,
          'tkey;shared.toast.delete.success.title',
          'tkey;admin.system.oauth2_resource_server_management.toast.delete.message',
        );
      }),
    );
  }

  private _initBottomNav(): void {
    const bottomNavButtons: BottomNavButton[] = [
      {
        id: 'deleteOauth2ResourceServerSettings',
        dataAutomation: 'oauth2-resource-server-management-delete-button',
        label: 'tkey;global.button.delete.text',
        buttonClass: ButtonStyles.DANGER,
        state: {
          pending: false,
          buttonDisableState: {
            disabled: !this.isExisting,
            tooltipKey: '',
          },
        },
        icon: ZiroIcons.DELETE_SMALL,
        cb: () => this._openDeleteModal(),
      },
      {
        id: 'saveOauth2ResourceServerSettings',
        dataAutomation: 'oauth2-resource-server-management-save-button',
        label: 'tkey;global.button.save.text',
        buttonClass: ButtonStyles.PRIMARY,
        state: {
          pending: false,
          buttonDisableState: {
            disabled: false,
            tooltipKey: '',
          },
        },
        icon: ZiroIcons.SAVE_SUCCESS,
        type: ButtonTypes.SUBMIT,
        submitSubject: this._validateAndSubmitSource,
      },
    ];

    this.bottomNavService.dispatch(new BottomNavUpdateButtonsList(bottomNavButtons));
  }

  private _setBottomNavValidationError(hasError: boolean): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateState({
        hasValidationError: hasError,
      }),
    );
  }

  private _setBottomNavPending(pendingButton: 'save' | 'delete' | 'none'): void {
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'deleteOauth2ResourceServerSettings',
        state: {
          pending: pendingButton === 'delete',
          buttonDisableState: {
            disabled: !this.isExisting || pendingButton !== 'none',
          },
        },
      }),
    );
    this.bottomNavService.dispatch(
      new BottomNavUpdateButtonState({
        id: 'saveOauth2ResourceServerSettings',
        state: {
          pending: pendingButton === 'save',
          buttonDisableState: {
            disabled: pendingButton !== 'none',
          },
        },
      }),
    );
  }
}
